import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LockIcon from '@mui/icons-material/Lock';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/components/ui/use-toast';

export function VerifyOtpPage() {
  const { t } = useTranslation();
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const navigate = useNavigate();
  const { otpState, tempToken, setUser, setTokens, clearOtpState } = useAuthStore();
  const { toast } = useToast();

  useEffect(() => {
    if (!otpState) {
      navigate('/login');
      return;
    }

    const expiresAt = new Date(otpState.expiresAt).getTime();
    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setTimeLeft(remaining);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [otpState, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      toast({
        title: t('common.error'),
        description: t('auth.enterValidOtp'),
        variant: 'destructive',
      });
      return;
    }

    if (!otpState || !tempToken) {
      toast({
        title: t('common.error'),
        description: t('auth.sessionExpired'),
        variant: 'destructive',
      });
      navigate('/login');
      return;
    }

    setIsLoading(true);

    try {
      const endpoint =
        otpState.purpose === 'register'
          ? '/api/v1/auth/register/verify-otp'
          : '/api/v1/auth/login/verify-otp';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tempToken}`,
        },
        body: JSON.stringify({ otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('auth.invalidOtp'));
      }

      clearOtpState();

      if (data.data.requiresRoleSelection) {
        setTokens(data.data.accessToken, data.data.refreshToken);
        navigate('/role-select');
      } else {
        setUser(data.data.user);
        setTokens(data.data.accessToken, data.data.refreshToken);

        toast({
          title: t('common.success'),
          description: t('auth.loginSuccess'),
          variant: 'success',
        });

        const redirectPath = data.data.user.role === 'admin' ? '/admin' : data.data.user.role === 'caregiver' ? '/caregiver' : '/family';
        navigate(redirectPath);
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.somethingWrong'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpState) return;

    setIsLoading(true);

    try {
      const endpoint =
        otpState.purpose === 'register'
          ? '/api/v1/auth/register/request-otp'
          : '/api/v1/auth/login/request-otp';

      const body =
        otpState.purpose === 'register'
          ? { email: otpState.email, fullName: otpState.fullName }
          : { email: otpState.email };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('auth.resendFailed'));
      }

      toast({
        title: t('auth.otpResent'),
        description: t('auth.newCodeSent'),
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.somethingWrong'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!otpState) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          {t('auth.verifyEmail')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('auth.codeSentTo')} <strong>{otpState.email}</strong>
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          id="otp"
          name="otp"
          type="text"
          label={t('auth.verificationCode')}
          placeholder="000000"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          disabled={isLoading}
          inputProps={{
            maxLength: 6,
            style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' },
          }}
          InputProps={{
            startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ mb: 2 }}
        />

        {timeLeft > 0 && (
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
            {t('auth.codeExpires')} {formatTime(timeLeft)}
          </Typography>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading || timeLeft === 0}
          sx={{ mb: 2, py: 1.5 }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('auth.verify')
          )}
        </Button>

        <Button
          fullWidth
          variant="text"
          onClick={handleResendOtp}
          disabled={isLoading || timeLeft > 0}
        >
          {t('auth.resendCode')}
        </Button>
      </Box>
    </Box>
  );
}
